/**
 * ResourceSettingsTab Component (T066 - US5)
 * Sub-tab with CRUD management for resource types, tags, and authors
 */

import { Card } from '@/shared/components/ui/card';
import {
  useResourceTypes,
  useCreateResourceType,
  useUpdateResourceType,
  useDeleteResourceType,
} from '../hooks/useAdminResourceTypes';
import {
  useResourceTags,
  useCreateResourceTag,
  useUpdateResourceTag,
  useDeleteResourceTag,
} from '../hooks/useAdminResourceTags';
import {
  useResourceAuthors,
  useCreateResourceAuthor,
  useUpdateResourceAuthor,
  useDeleteResourceAuthor,
} from '../hooks/useAdminResourceAuthors';
import { SettingsSection } from './SettingsSection';

export function ResourceSettingsTab() {
  const { data: types = [], isLoading: typesLoading } = useResourceTypes();
  const createType = useCreateResourceType();
  const updateType = useUpdateResourceType();
  const deactivateType = useDeleteResourceType();

  const { data: tags = [], isLoading: tagsLoading } = useResourceTags();
  const createTag = useCreateResourceTag();
  const updateTag = useUpdateResourceTag();
  const deactivateTag = useDeleteResourceTag();

  const { data: authors = [], isLoading: authorsLoading } = useResourceAuthors();
  const createAuthor = useCreateResourceAuthor();
  const updateAuthor = useUpdateResourceAuthor();
  const deleteAuthor = useDeleteResourceAuthor();

  return (
    <div data-testid="resource-settings-tab" className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Resources Settings</h2>
        <p className="text-gray-600 mt-1">
          Manage reference data for your resources
        </p>
      </div>

      <Card className="p-6">
        <SettingsSection
          title="Types"
          items={types}
          isLoading={typesLoading}
          placeholderText="Enter type name"
          onCreate={(p) => createType.mutateAsync(p)}
          onUpdate={(p) => updateType.mutateAsync(p)}
          onAction={(p) => deactivateType.mutateAsync(p)}
          actionType="deactivate"
          createPending={createType.isPending}
        />
      </Card>

      <Card className="p-6">
        <SettingsSection
          title="Tags"
          items={tags}
          isLoading={tagsLoading}
          placeholderText="Enter tag name"
          onCreate={(p) => createTag.mutateAsync(p)}
          onUpdate={(p) => updateTag.mutateAsync(p)}
          onAction={(p) => deactivateTag.mutateAsync(p)}
          actionType="deactivate"
          createPending={createTag.isPending}
        />
      </Card>

      <Card className="p-6">
        <SettingsSection
          title="Authors"
          items={authors}
          isLoading={authorsLoading}
          placeholderText="Enter author name"
          onCreate={(p) => createAuthor.mutateAsync(p)}
          onUpdate={(p) => updateAuthor.mutateAsync(p)}
          onAction={({ id }) => deleteAuthor.mutateAsync(id)}
          actionType="delete"
          createPending={createAuthor.isPending}
        />
      </Card>
    </div>
  );
}
